/**
 * @fileoverview The Needs Attention section of the Sales Dashboard.
 * @description Lists every Overdue or At Risk target the backend returned, in the backend's order
 * (Overdue first, longest overdue first, then At Risk, soonest due first), each opening My Page for
 * the caller's own targets or Sales Performance for a wider scope. The count is the true total, so
 * when the backend capped the rows the section says how many are shown. Nothing here sorts or
 * recomputes a status.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { dueDateLabel } from '@/features/sales/lib/sales-dashboard.utils'
import { ROUTES } from '@/shared/constants/routes.constants'

import AttentionList from './AttentionList'

import type {
  DashboardAttentionItem,
  DashboardNeedsAttention,
} from '@/features/sales/types/sales-dashboard.types'
import type { AttentionItem } from '@/features/sales/types/sales.types'
import type { TFunction } from 'i18next'

interface DashboardAttentionSectionProps {
  needsAttention: DashboardNeedsAttention
}

/**
 * @description One backend row as a list item.
 * @param {TFunction} t - The translator.
 * @param {DashboardAttentionItem} item - The target.
 * @param {boolean} own - Whether the list covers only the caller's own targets.
 * @returns {AttentionItem & { route: string }} The item and the page it opens.
 */
function toListItem(
  t: TFunction,
  language: string,
  item: DashboardAttentionItem,
  own: boolean,
): AttentionItem & { route: string } {
  const overdue = item.status === 'OVERDUE'
  return {
    id: item.targetId,
    title: item.bank,
    detail: t('sales.dashboard.attention.due', { date: dueDateLabel(item.dueDate, language) }),
    where: item.owner.name,
    tag: t(
      overdue ? 'sales.dashboard.attention.overdueTag' : 'sales.dashboard.attention.atRiskTag',
    ),
    tone: overdue ? 'danger' : 'warning',
    dest: own ? 'myPage' : 'performance',
    route: own ? ROUTES.SALES_MY_PAGE : ROUTES.SALES_PERFORMANCE,
  }
}

/**
 * @component
 * @param {DashboardAttentionSectionProps} props - The section.
 * @param {DashboardNeedsAttention} props.needsAttention - The backend's Needs Attention section.
 */
function DashboardAttentionSection({ needsAttention }: DashboardAttentionSectionProps) {
  const { t, i18n } = useTranslation()
  const { count, items, scope } = needsAttention
  const own = scope === 'OWN'

  return (
    <section
      className="rounded-[10px] border border-divider bg-white"
      data-testid="dashboard-attention"
    >
      <div className="flex flex-wrap items-baseline gap-3 px-4 py-4 sm:px-5">
        <span className="text-[15px] font-bold text-ink">
          {t('sales.dashboard.attention.title')}
        </span>
        <span className="h-3.5 w-px bg-rule" />
        <span className="text-xs text-slate" data-testid="dashboard-attention-count">
          {t(own ? 'sales.dashboard.attention.countOwn' : 'sales.dashboard.attention.count', {
            count,
          })}
        </span>
        {count > items.length && (
          <span className="text-xs text-footer" data-testid="dashboard-attention-capped">
            {t('sales.dashboard.attention.showing', { shown: items.length })}
          </span>
        )}
      </div>
      {items.length === 0 ? (
        <p
          className="border-t border-divider px-4 py-6 text-[13px] text-slate sm:px-5"
          data-testid="dashboard-attention-empty"
        >
          {t('sales.dashboard.attention.empty')}
        </p>
      ) : (
        <AttentionList items={items.map((item) => toListItem(t, i18n.language, item, own))} />
      )}
    </section>
  )
}

export default DashboardAttentionSection
